import { ref, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { globalState, updateOrgSettings, updateWhitelabelSettings } from '@/globalState';
import axios from 'axios';
import { useI18n } from '@/i18n';
import { isDefinedAndNotEmpty } from '../../../isDefinedAndNotEmpty.js';
import { useToast } from 'vue-toastification';
import { logoutSession } from '@/api/accountsService';
import { initDB, saveOrgStyles } from '@/indexedDBService';
import { getLogger } from '@/shared/tools/logService.js';

const log = getLogger('useLoginModel.js');

export function useLoginModel() {
  const { t, currentLanguage } = useI18n();
  const router = useRouter();
  const route = useRoute();
  const toast = useToast();

  const userName = ref('');
  const password = ref('');
  const organizationName = ref('');
  const errorMessage = ref('');

  const mfaStep = ref(false);
  const mfaMethod = ref('');
  const otpSessionId = ref('');
  const otpCode = ref('');

  const isLoading = ref(false);
  const isOtpLoading = ref(false);

  const otpDigits = ref(['', '', '', '', '', '']);
  const otpRefs = ref([]);

  const authAppSetupStep = ref(false);
  const authAppQrCode = ref('');
  const authAppSecret = ref('');

  const safeAuthAppQrCode = computed(() => {
    const raw = authAppQrCode.value;
    if (typeof raw !== 'string') {
      return '';
    }
    const s = raw.trim();
    if (s.length === 0) {
      return '';
    }
    if (s.startsWith('data:image/') === true) {
      return s;
    }
    if (typeof window === 'undefined' || window.location === undefined || window.location === null) {
      return '';
    }
    let urlObj;
    try {
      urlObj = new URL(s, window.location.origin);
    } catch (e) {
      return '';
    }
    const proto = typeof urlObj.protocol === 'string' ? urlObj.protocol.toLowerCase() : '';
    const isProd = process.env.environment === 'production';
    if (proto === 'https:') {
      return urlObj.href;
    }
    if (proto === 'http:' && isProd === false) {
      return urlObj.href;
    }
    return '';
  });

  const mfaTitle = computed(() => t('login.twoFactorAuthentication', 'Two-Factor Authentication'));

  const mfaDescription = computed(() => {
    if (mfaMethod.value === 'AuthApp') {
      return t('login.enterAuthAppCode', 'Enter the code from your authenticator app');
    }
    if (mfaMethod.value === 'Outlook365MailOTP') {
      return t('login.enterOtp', 'Enter the verification code sent to your email');
    }
    return t('login.enterVerificationCode', 'Enter your verification code');
  });

  const mfaInputLabel = computed(() => {
    if (mfaMethod.value === 'AuthApp') {
      return t('login.authAppCodeLabel', 'Authenticator code');
    }
    return t('login.verificationCode', 'Verification Code');
  });

  const mfaButtonLabel = computed(() => {
    if (mfaMethod.value === 'AuthApp') {
      return t('login.verifyAuthAppCode', 'Verify code');
    }
    return t('login.verifyOtp', 'Verify');
  });

  const isRTL = computed(() => currentLanguage.value === 'he' || currentLanguage.value === 'ar');

  onMounted(() => {
    setTimeout(() => {
      toast.clear();
    }, 200);
  });

  function resetOtpDigits() {
    otpDigits.value = ['', '', '', '', '', ''];
  }

  function resetMfaState() {
    mfaStep.value = false;
    mfaMethod.value = '';
    otpSessionId.value = '';
    resetOtpDigits();
    isOtpLoading.value = false;
  }

  async function clearSessionCookies() {
    deleteCookieMaxAge('jwtToken');
    deleteCookieMaxAge('userData');
    try {
      await logoutSession();
    } catch (e) {}
    globalState.userInfo = null;
  }

  function getSafeRedirectPath(value) {
    if (typeof value !== 'string') {
      return null;
    }
    const s = value.trim();
    if (s.length === 0) {
      return null;
    }
    if (s.startsWith('/') === false) {
      return null;
    }
    if (s.startsWith('//') === true) {
      return null;
    }
    if (s.indexOf('://') >= 0) {
      return null;
    }
    if (s.indexOf('\\') >= 0) {
      return null;
    }
    return s;
  }

  function setCookieMaxAge(name, value, maxAgeSeconds, path = '/', sameSite = 'Lax') {
    if (typeof document === 'undefined') {
      return;
    }
    const secure = typeof window !== 'undefined' && window.location !== undefined && window.location !== null && window.location.protocol === 'https:';
    const safeName = encodeURIComponent(String(name));
    const safeValue = encodeURIComponent(String(value));
    const maxAge = Number.isFinite(Number(maxAgeSeconds)) === true ? Math.max(0, Math.floor(Number(maxAgeSeconds))) : 0;
    const isProd = process.env.environment === 'production';
    
    let sameSiteValue = sameSite;
    if (isProd === true && typeof window !== 'undefined' && window.location) {
      const currentHost = window.location.hostname;
      const isCrossDomain = currentHost.includes('dialmyapp.com') === true;
      if (isCrossDomain === true) {
        sameSiteValue = 'Lax';
      }
    }

    const sameSiteStr = isProd === true ? `; SameSite=${sameSiteValue}` : '';
    let cookie = `${safeName}=${safeValue}; Path=${path}; Max-Age=${maxAge}${sameSiteStr}`;
    if (secure === true) {
      cookie = cookie + '; Secure';
    }
    document.cookie = cookie;
  }

  function deleteCookieMaxAge(name) {
    setCookieMaxAge(name, '', 0, '/', 'Strict');
    if (typeof window !== 'undefined' && window.location !== undefined && window.location !== null && window.location.protocol === 'https:') {
      setCookieMaxAge(name, '', 0, '/', 'Strict');
    }
  }

  function returnToLogin() {
    authAppSetupStep.value = false;
    authAppQrCode.value = '';
    authAppSecret.value = '';
    resetMfaState();
    errorMessage.value = '';
  }

  function isProdEnv() {
    return process.env.environment === 'production';
  }

  function stripControlChars(value) {
    const s = typeof value === 'string' ? value : '';
    return s.replace(/[\u0000-\u001F\u007F]/g, '');
  }

  function normalizeLoginText(value, maxLen) {
    const cleaned = stripControlChars(value).trim();
    if (cleaned.length > maxLen) {
      return cleaned.slice(0, maxLen);
    }
    return cleaned;
  }

  function hasDisallowedLoginChars(value) {
    if (typeof value !== 'string') {
      return true;
    }
    if (/[<>]/.test(value) === true) {
      return true;
    }
    return false;
  }

  function getLoginPayloadOrNull() {
    const u = normalizeLoginText(userName.value, 256);
    const pRaw = typeof password.value === 'string' ? password.value : '';
    const pw = pRaw.length > 2048 ? pRaw.slice(0, 2048) : pRaw;
    const org = normalizeLoginText(organizationName.value, 256);

    if (u.length === 0 || pw.length === 0) {
      errorMessage.value = t('login.missingCredentials', 'Username and password are required');
      return null;
    }

    if (hasDisallowedLoginChars(u) === true) {
      errorMessage.value = t('login.invalidUsername', 'Username contains invalid characters.');
      return null;
    }

    if (org.length > 0 && hasDisallowedLoginChars(org) === true) {
      errorMessage.value = t('login.invalidOrganization', 'Invalid organization');
      return null;
    }

    return {
      username: u,
      password: pw,
      organizationName: org.length > 0 ? org : null
    };
  }

  function getLoginFailureMessage(response, data) {
    const rawError = data !== null && data !== undefined && typeof data.error === 'string' ? data.error : '';
    const retryAfterMinutes = data !== null && data !== undefined ? Number(data.retryAfterMinutes) : NaN;
    if (response.status === 429 && rawError === 'Too many login attempts. Please try again later.') {
      if (Number.isFinite(retryAfterMinutes) === true && retryAfterMinutes > 0) {
        return t('login.tooManyAttemptsWithTime', 'Too many login attempts. Please try again in {minutes} minutes.', { minutes: String(Math.ceil(retryAfterMinutes)) });
      }
      return t('login.tooManyAttempts', 'Too many login attempts. Please try again later.');
    }
    return t('login.wrongCredentials', 'Wrong credentials');
  }

  async function login() {
    if (isLoading.value === true) {
      return;
    }

    const apiBase = typeof process.env.VUE_APP_API_URL === 'string' ? process.env.VUE_APP_API_URL : '';
    const url = `${apiBase}/api/users/login`;

    errorMessage.value = '';
    const payload = getLoginPayloadOrNull();
    if (payload === null) {
      return;
    }

    isLoading.value = true;

    try {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.aadRequired === true) {
        errorMessage.value = typeof data.error === 'string' && data.error !== '' ? data.error : t('login.aadRequired', 'Microsoft Entra ID sign-in is required for this organization.');
        isLoading.value = false;
        return;
      }

      if (data.mfaSetupRequired === true && data.method === 'AuthApp') {
        authAppSetupStep.value = true;
        authAppQrCode.value = isDefinedAndNotEmpty(data.qrCodeUrl) === true ? data.qrCodeUrl : '';
        authAppSecret.value = isDefinedAndNotEmpty(data.secret) === true ? data.secret : '';
        mfaMethod.value = 'AuthApp';
        resetMfaState();
        if (data.forceLogout === true) {
          await clearSessionCookies();
        }
        isLoading.value = false;
        return;
      }

      if (data.mfaRequired === true && data.method === 'AuthApp') {
        mfaStep.value = true;
        mfaMethod.value = 'AuthApp';
        authAppSetupStep.value = false;
        otpSessionId.value = '';
        resetOtpDigits();
        isLoading.value = false;
        return;
      }

      if (data.mfaRequired === true && data.method === 'Outlook365MailOTP' && isDefinedAndNotEmpty(data.otpSessionId) === true) {
        mfaStep.value = true;
        mfaMethod.value = 'Outlook365MailOTP';
        authAppSetupStep.value = false;
        otpSessionId.value = data.otpSessionId;
        resetOtpDigits();
        isLoading.value = false;
        return;
      }

      if (response.ok === false) {
        errorMessage.value = getLoginFailureMessage(response, data);
        isLoading.value = false;
        return;
      }

      await handleSuccessfulLogin(data);
      isLoading.value = false;
    } catch (error) {
      if (isProdEnv() === false) {
        log.error('Login failed (Network/Fetch Error):', error);
      }
      errorMessage.value = t('login.loginFailedNetwork', 'Login failed. Please try again.');
      isLoading.value = false;
    }
  }

  async function submitMfaCode() {
    const apiBase = typeof process.env.VUE_APP_API_URL === 'string' ? process.env.VUE_APP_API_URL : '';
    const url = `${apiBase}/api/users/login`;

    errorMessage.value = '';
    isOtpLoading.value = true;

    otpCode.value = Array.isArray(otpDigits.value) === true ? otpDigits.value.join('') : '';
    if (typeof otpCode.value !== 'string' || otpCode.value.length < 6) {
      errorMessage.value = t('login.invalidOtp', 'Please enter the full code');
      isOtpLoading.value = false;
      return;
    }

    const payload = getLoginPayloadOrNull();
    if (payload === null) {
      isOtpLoading.value = false;
      return;
    }

    if (mfaMethod.value === 'Outlook365MailOTP') {
      if (isDefinedAndNotEmpty(otpSessionId.value) === false) {
        errorMessage.value = t('login.loginFailedProcessData', 'Login failed');
        isOtpLoading.value = false;
        return;
      }
      payload.otpSessionId = otpSessionId.value;
      payload.otpCode = otpCode.value;
    } else if (mfaMethod.value === 'AuthApp') {
      payload.authAppCode = otpCode.value;
    } else {
      errorMessage.value = t('login.loginFailedProcessData', 'Login failed');
      isOtpLoading.value = false;
      return;
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok === false) {
        errorMessage.value = getLoginFailureMessage(response, data);
        isOtpLoading.value = false;
        return;
      }

      await handleSuccessfulLogin(data);
      isOtpLoading.value = false;
    } catch (error) {
      errorMessage.value = t('login.loginFailedNetwork', 'Login failed. Please try again.');
      isOtpLoading.value = false;
    }
  }

  function setOtpRef(el, index) {
    if (typeof index === 'number') {
      if (el !== null && el !== undefined) {
        otpRefs.value[index] = el;
      }
    }
  }

  function focusOtpIndex(index) {
    if (typeof index === 'number') {
      const el = otpRefs.value[index];
      if (el !== null && el !== undefined) {
        el.focus();
        if (typeof el.select === 'function') {
          el.select();
        }
      }
    }
  }

  function onOtpInput(index, event) {
    const target = event !== null && event !== undefined ? event.target : null;
    const value = target !== null && target !== undefined ? target.value : '';
    const digit = typeof value === 'string' ? value.replace(/\D/g, '').slice(0, 1) : '';
    otpDigits.value[index] = digit;
    if (digit !== '') {
      if (index < 5) {
        focusOtpIndex(index + 1);
      }
    }
  }

  function onOtpKeydown(index, event) {
    const key = event !== null && event !== undefined ? event.key : '';
    if (key === 'Backspace') {
      if (otpDigits.value[index] === '') {
        if (index > 0) {
          focusOtpIndex(index - 1);
        }
      } else {
        otpDigits.value[index] = '';
      }
    }

    if (key === 'ArrowLeft') {
      if (index > 0) {
        focusOtpIndex(index - 1);
      }
    }

    if (key === 'ArrowRight') {
      if (index < 5) {
        focusOtpIndex(index + 1);
      }
    }
  }

  function onOtpPaste(event, startIndex) {
    const clipboardData = event !== null && event !== undefined ? event.clipboardData : null;
    const text = clipboardData !== null && clipboardData !== undefined ? clipboardData.getData('text') : '';
    const digits = typeof text === 'string' ? text.replace(/\D/g, '') : '';

    let i = 0;
    while (i < digits.length && startIndex + i < 6) {
      otpDigits.value[startIndex + i] = digits.charAt(i);
      i = i + 1;
    }

    const nextIndex = startIndex + i < 6 ? startIndex + i : 5;
    focusOtpIndex(nextIndex);

    if (event !== null && event !== undefined) {
      if (typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
    }
  }

  async function handleSuccessfulLogin(data) {
    resetMfaState();
    authAppSetupStep.value = false;
    authAppQrCode.value = '';
    authAppSecret.value = '';
    deleteCookieMaxAge('jwtToken');
    deleteCookieMaxAge('userData');
    deleteCookieMaxAge('authToken');

    const rawToken = typeof data?.token === 'string' ? data.token.trim() : '';
    if (data?.clientCookieAuth === true && rawToken.length > 0) {
      setCookieMaxAge('authToken', rawToken, 7200, '/', 'Lax');
    }

    let userInfoFromLogin = null;
    if (data !== null && data !== undefined && data.user !== undefined && data.user !== null) {
      const u = data.user;
      userInfoFromLogin = {
        userId: u.userId,
        organizationID: u.organizationID,
        organizationName: u.organizationName,
        userName: u.userName,
        permissions: Array.isArray(u.permissions) === true ? u.permissions : [],
        roles: Array.isArray(u.roles) === true ? u.roles : [],
        allowedSources: Array.isArray(u.allowedSources) === true ? u.allowedSources : [],
        subscription: typeof u.subscription === 'string' ? u.subscription : '',
        subscriptionConfig: typeof u.subscriptionConfig === 'object' && u.subscriptionConfig !== null ? u.subscriptionConfig : null,
        passwordChangeRequired: u.passwordChangeRequired === true
      };
    }
    globalState.userInfo = userInfoFromLogin;

    try {
      await initDB();
      const resp = await axios.get(`${globalState.apiEndpoint}/api/customDesigns`, { withCredentials: true });
      const raw = resp !== null && resp !== undefined ? resp.data : null;
      const cssContent = raw !== null && raw !== undefined && raw.cssContent !== undefined && raw.cssContent !== null ? raw.cssContent : '';
      const jsContent = raw !== null && raw !== undefined && raw.jsContent !== undefined && raw.jsContent !== null ? raw.jsContent : '';
      await saveOrgStyles({ css: cssContent, js: jsContent });
    } catch (e) {
      try {
        await saveOrgStyles({ css: '', js: '' });
      } catch (e2) {}
    }

    globalState.triggerOrgStyleRefresh = Date.now();

    try {
      const organizationId = globalState.userInfo !== null && globalState.userInfo !== undefined ? globalState.userInfo.organizationID : undefined;
      if (organizationId !== undefined && organizationId !== null) {
        const settingsResponse = await axios.get(`${globalState.apiEndpoint}/api/organizationSettings/public/${organizationId}`);
        if (settingsResponse.data !== undefined) {
          updateOrgSettings(settingsResponse.data.timezone, settingsResponse.data.language, settingsResponse.data.allowedSources);
          updateWhitelabelSettings(settingsResponse.data.whiteLabelEnabled === true, settingsResponse.data.whiteLabelLogoUrl, settingsResponse.data.whiteLabelAppName);
        }
      }

      const redirectRaw = route.query.redirect;
      const redirect = getSafeRedirectPath(typeof redirectRaw === 'string' ? redirectRaw : '');
      if (redirect !== null) {
        router.push(redirect);
      } else {
        router.push('/');
      }
    } catch (postLoginError) {
      const redirectRaw = route.query.redirect;
      const redirect = getSafeRedirectPath(typeof redirectRaw === 'string' ? redirectRaw : '');
      if (redirect !== null) {
        router.push(redirect);
      } else {
        router.push('/');
      }
    }
  }

  function parseJWTToken(token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        userId: payload.userID !== undefined && payload.userID !== null ? payload.userID : payload.userId,
        organizationID: payload.organizationID !== undefined && payload.organizationID !== null ? payload.organizationID : payload.organizationId,
        organizationName: payload.organizationName !== undefined && payload.organizationName !== null ? payload.organizationName : payload.organizationname,
        userName: payload.username !== undefined && payload.username !== null ? payload.username : payload.userName,
        permissions: isDefinedAndNotEmpty(payload.permissions) === true ? payload.permissions : [],
        roles: Array.isArray(payload.roles) === true ? payload.roles : [],
        subscription: typeof payload.subscription === 'string' ? payload.subscription : ''
      };
    } catch (error) {
      if (isProdEnv() === false) {
        log.error('Error parsing JWT token:', error);
      }
      return null;
    }
  }

  return {
    t,
    currentLanguage,
    userName,
    password,
    organizationName,
    errorMessage,
    mfaStep,
    mfaMethod,
    otpSessionId,
    otpDigits,
    authAppSetupStep,
    authAppQrCode,
    authAppSecret,
    safeAuthAppQrCode,
    mfaTitle,
    mfaDescription,
    mfaInputLabel,
    mfaButtonLabel,
    isRTL,
    isLoading,
    isOtpLoading,
    login,
    submitMfaCode,
    returnToLogin,
    setOtpRef,
    onOtpInput,
    onOtpKeydown,
    onOtpPaste
  };
}


